import React from 'react';

const formatNumber = (value, decimals = 2) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '–';
  }
  return value.toFixed(decimals);
};

const SimplePriceBreakdown = ({ breakdown }) => {
  const isPartnerTier = Boolean(breakdown.partner_tier_details);
  const minChargeThreshold =
    breakdown.min_charge_threshold ?? breakdown.minimum_charge_threshold;
  const hasPieces = Array.isArray(breakdown.pieces) && breakdown.pieces.length > 0;

  if (hasPieces && breakdown.job_type === 'mounted-boards') {
    const tier = breakdown.tier_pricing || {};
    return (
      <div className="space-y-3">
        <table className="w-full"><tbody>
          <tr className="border-b"><td className="py-2">Raw Material Area</td><td className="py-2 text-right">{formatNumber(breakdown.raw_material_area_sqm, 4)} sqm</td></tr>
          <tr className="border-b"><td className="py-2">Physical Materials</td><td className="py-2 text-right">£{formatNumber(breakdown.physical_material_cost)}</td></tr>
          <tr className="border-b text-green-600"><td className="py-2">Material Volume Discount</td><td className="py-2 text-right">-£{formatNumber(breakdown.material_volume_discount_amount)}</td></tr>
          <tr className="border-b"><td className="py-2">Handling</td><td className="py-2 text-right">£{formatNumber(breakdown.handling_cost)}</td></tr>
          <tr className="border-b"><td className="py-2">Explicit Finishing</td><td className="py-2 text-right">£{formatNumber(breakdown.finishing_cost)}</td></tr>
          <tr className="border-b"><td className="py-2">Global Multiplier ({formatNumber(breakdown.global_price_multiplier, 2)}x)</td><td className="py-2 text-right">+£{formatNumber(breakdown.global_uplift_before_min_charge)}</td></tr>
          <tr className="border-b"><td className="py-2">Retail Total</td><td className="py-2 text-right">£{formatNumber(tier.retail_total ?? breakdown.retail_price)}</td></tr>
          {breakdown.min_charge_applied && <tr className="border-b text-orange-600"><td className="py-2">Retail Minimum Adjustment</td><td className="py-2 text-right">+£{formatNumber(breakdown.min_charge_adjustment)}</td></tr>}
          {tier.discount_percent > 0 && <tr className="border-b text-green-600"><td className="py-2">{tier.tier} Discount ({formatNumber(tier.discount_percent)}%)</td><td className="py-2 text-right">-£{formatNumber(tier.discount_amount)}</td></tr>}
          <tr className="bg-green-50 font-bold text-green-800"><td className="py-3">Final Total</td><td className="py-3 text-right">£{formatNumber(tier.final_total ?? breakdown.total_cost)}</td></tr>
        </tbody></table>
        {breakdown.pieces.map((piece) => <div key={piece.piece_number} className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm"><div className="flex justify-between font-medium"><span>Piece {piece.piece_number}</span><span>£{formatNumber(piece.retail_subtotal)}</span></div><div className="mt-1 flex justify-between text-xs text-gray-600"><span>{piece.dimensions} x {piece.quantity}</span><span>{formatNumber(piece.raw_area_sqm, 4)} sqm</span></div>{piece.materials.map((material) => <div key={material.id} className="mt-1 flex justify-between text-xs text-gray-600"><span>{material.name} ({formatNumber(material.price_per_sqm)}/sqm)</span><span>£{formatNumber(material.total_cost)}</span></div>)}</div>)}
      </div>
    );
  }

  if (hasPieces) {
    return (
      <div className="space-y-4">
        <table className="w-full">
          <tbody>
            {breakdown.job_type && (
              <tr className="border-b">
                <td className="py-2 font-medium">Job Type</td>
                <td className="py-2 text-right">{breakdown.job_type}</td>
              </tr>
            )}
            <tr className="border-b">
              <td className="py-2">Total Pieces</td>
              <td className="py-2 text-right">{breakdown.total_pieces}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2">Total Items</td>
              <td className="py-2 text-right">{breakdown.total_items}</td>
            </tr>
            <tr className="border-b">
              <td className="py-2">Subtotal</td>
              <td className="py-2 text-right">
                £{formatNumber(breakdown.total_cost_before_min_charge ?? breakdown.sub_total)}
              </td>
            </tr>
            {minChargeThreshold !== undefined && breakdown.min_charge_applied && (
              <tr className="border-b text-orange-600">
                <td className="py-2">Minimum Charge Applied</td>
                <td className="py-2 text-right">
                  Adjusted to £{formatNumber(minChargeThreshold)}
                </td>
              </tr>
            )}
            {isPartnerTier ? (
              <>
                <tr className="border-b bg-gray-50 font-semibold">
                  <td className="py-3">Retail Price</td>
                  <td className="py-3 text-right">
                    £{formatNumber(breakdown.partner_tier_details.original_retail_price)}
                  </td>
                </tr>
                <tr className="border-b text-green-600">
                  <td className="py-2">
                    Partner Discount ({breakdown.partner_tier_details.discount_percent}%)
                  </td>
                  <td className="py-2 text-right">
                    -£{formatNumber(
                      breakdown.partner_tier_details.original_retail_price - breakdown.total_cost
                    )}
                  </td>
                </tr>
                <tr className="bg-green-50 font-bold text-green-800">
                  <td className="py-3">Partner Price</td>
                  <td className="py-3 text-right">£{formatNumber(breakdown.total_cost)}</td>
                </tr>
              </>
            ) : (
              <tr className="bg-gray-50 font-bold">
                <td className="py-3">Final Price</td>
                <td className="py-3 text-right">£{formatNumber(breakdown.total_cost)}</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="space-y-2">
          {breakdown.pieces.map((piece, index) => {
            const pieceTotal = piece.sub_total_before_min_charge ?? piece.piece_cost;
            const pieceRetail =
              typeof pieceTotal === 'number' && !Number.isNaN(pieceTotal)
                ? pieceTotal / 0.625
                : null;

            return (
              <div
                key={piece.piece_number ?? index}
                className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
              >
                <div className="flex justify-between">
                  <span className="font-medium">
                    Piece {piece.piece_number ?? index + 1}
                  </span>
                  <span>{piece.job_type || '—'}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600 mt-1">
                  <span>{piece.dimensions || '–'}</span>
                  <span>Qty: {piece.quantity ?? '–'}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600 mt-1">
                  <span>Materials</span>
                  <span>
                    £{formatNumber(piece.materials_cost)} / Finishing £{formatNumber(piece.finishing_cost)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-semibold mt-2">
                  <span>Piece Total</span>
                  <span>
                    £{formatNumber(pieceTotal)}
                    {isPartnerTier && pieceRetail !== null && (
                      <span className="text-red-600 font-medium ml-2">
                        (Retail: £{formatNumber(pieceRetail)})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2">Component</th>
          <th className="text-right py-2">Value</th>
        </tr>
      </thead>
      <tbody>
        {breakdown.job_type && (
          <tr className="border-b">
            <td className="py-2">Job Type</td>
            <td className="text-right">{breakdown.job_type}</td>
          </tr>
        )}
        <tr className="border-b">
          <td className="py-2">Dimensions</td>
          <td className="text-right">{breakdown.dimensions}</td>
        </tr>
        <tr className="border-b">
          <td className="py-2">Quantity</td>
          <td className="text-right">{breakdown.quantity}</td>
        </tr>
        {typeof breakdown.area_used_for_pricing_sqm === 'number' && (
          <tr className="border-b">
            <td className="py-2">Area Used for Pricing</td>
            <td className="text-right">
              {formatNumber(breakdown.area_used_for_pricing_sqm)} sqm
            </td>
          </tr>
        )}
        {breakdown.materials_cost > 0 && (
          <tr className="border-b">
            <td className="py-2">Materials Cost</td>
            <td className="text-right">£{formatNumber(breakdown.materials_cost)}</td>
          </tr>
        )}
        {breakdown.print_cost > 0 && (
          <tr className="border-b">
            <td className="py-2">Print Cost</td>
            <td className="text-right">£{formatNumber(breakdown.print_cost)}</td>
          </tr>
        )}
        {breakdown.finishing_cost > 0 && (
          <tr className="border-b">
            <td className="py-2">Finishing Cost</td>
            <td className="text-right">£{formatNumber(breakdown.finishing_cost)}</td>
          </tr>
        )}
        {typeof breakdown.sub_total_before_min_charge === 'number' && (
          <tr className="border-b font-semibold">
            <td className="py-2">Subtotal</td>
            <td className="text-right">£{formatNumber(breakdown.sub_total_before_min_charge)}</td>
          </tr>
        )}
        {minChargeThreshold !== undefined && breakdown.min_charge_applied && (
          <tr className="border-b text-orange-600">
            <td className="py-2">Minimum Charge Applied</td>
            <td className="text-right">
              Total adjusted to £{formatNumber(minChargeThreshold)}
            </td>
          </tr>
        )}

        {isPartnerTier ? (
          <>
            <tr className="border-b bg-gray-50 font-semibold">
              <td className="py-3">Retail Price</td>
              <td className="text-right">
                £{formatNumber(breakdown.partner_tier_details.original_retail_price)}
              </td>
            </tr>
            <tr className="border-b text-green-600">
              <td className="py-2">
                Partner Discount ({breakdown.partner_tier_details.discount_percent}%)
              </td>
              <td className="text-right">
                -£{formatNumber(
                  breakdown.partner_tier_details.original_retail_price - breakdown.total_cost
                )}
              </td>
            </tr>
            <tr className="bg-green-50 font-bold text-green-800">
              <td className="py-3">Partner Price</td>
              <td className="text-right">£{formatNumber(breakdown.total_cost)}</td>
            </tr>
          </>
        ) : (
          <tr className="bg-gray-50 font-bold">
            <td className="py-3">Final Price</td>
            <td className="text-right">£{formatNumber(breakdown.total_cost)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default SimplePriceBreakdown;
